/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState, useId } from 'react';
import { injectIntl } from '../../../util/reactIntl';
import { formatCurrencyMajorUnit } from '../../../util/currency';
import useIsMobile from '../../../hooks/useIsMobile';
import { useHistory, useLocation } from 'react-router-dom';
import { IconArrowHead } from '../../../components';
import PriceFilterForm from '../PriceFilterForm/PriceFilterForm';

import css from './PriceFilterPlain.module.css';
import classNames from 'classnames';

const RADIX = 10;

const getPriceQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'price';
};

const parse = priceRange => {
  if (!priceRange) return null;

  const [minPrice, maxPrice] = priceRange.split(',').map(v => Number.parseInt(v, RADIX));

  const result = {};

  if (minPrice != null && !Number.isNaN(minPrice)) {
    result.minPrice = minPrice;
  }

  if (maxPrice != null && !Number.isNaN(maxPrice)) {
    result.maxPrice = maxPrice;
  }

  return Object.keys(result).length ? result : null;
};

// Format value, which should look like { minPrice, maxPrice }
const format = (range, queryParamName) => {
  const { minPrice, maxPrice } = range || {};
  // Note: we compare to null, because 0 as minPrice is falsy in comparisons.
  const value =
    minPrice && maxPrice
      ? `${minPrice},${maxPrice}`
      : minPrice
      ? `${minPrice},`
      : maxPrice
      ? `,${maxPrice}`
      : null;

  return { [queryParamName]: value };
};

/**
 * PriceFilterPlainComponent (functional) — mobile-only exclusive rendering via custom events
 */
const PriceFilterPlainComponent = props => {
  const {
    rootClassName,
    className,
    id,
    label,
    queryParamNames,
    initialValues,
    min,
    max,
    step,
    intl,
    marketplaceCurrency,
    inputIcon,
    onSubmit,
    onBlur,
    onToggle,
  } = props;

  // stable drawer key: prefer provided id, otherwise generated
  const reactId = useId();
  const drawerKey = id ?? reactId;

  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef(null);
  const filterContent = useRef(null);

  const isMobile = useIsMobile();

  // router hooks for cleaning other ranges in query
  const history = useHistory();
  const location = useLocation();

  // Helpers (moved inside component so they can use `location`)
  const buildCleanedQueryWithRange = (
    newRangeValueObj /* e.g. { [paramName]: '10,20' } */,
    paramName
  ) => {
    const params = new URLSearchParams(location.search);
    const cleaned = {};

    for (const [key, value] of params.entries()) {
      if (key === paramName) continue;
      if (key === 'page') continue; // reset pagination on filter change
      cleaned[key] = value;
    }
    // merge current/updated range (if newRangeValueObj has a null value, it means remove)
    if (newRangeValueObj && Object.prototype.hasOwnProperty.call(newRangeValueObj, paramName)) {
      const val = newRangeValueObj[paramName];
      if (val != null) {
        cleaned[paramName] = val;
      } else {
        // explicit removal: nothing to add
      }
    }
    return cleaned;
  };

  const handleChange = values => {
    const priceQueryParamName = getPriceQueryParamName(queryParamNames);
    const formatted = format(values, priceQueryParamName); // { [param]: 'min,max' } or { [param]: null }

    if (queryParamNames) {
      const cleanedQuery = buildCleanedQueryWithRange(formatted, priceQueryParamName);
      const params = new URLSearchParams();
      Object.entries(cleanedQuery).forEach(([k, v]) => {
        if (v != null) params.set(k, v);
      });

      history.replace({
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      });

      // still call optional onSubmit for side-effects if consumer passed one
      onSubmit?.(formatted);
      onBlur?.();
      return;
    }

    onSubmit?.(format(values, priceQueryParamName));
  };

  const handleClear = () => {
    const priceQueryParamName = getPriceQueryParamName(queryParamNames);

    if (queryParamNames) {
      const params = new URLSearchParams(location.search);
      params.delete(priceQueryParamName);

      history.replace({
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      });

      onSubmit?.(format(null, priceQueryParamName));
      onBlur?.();
      return;
    }

    onSubmit?.(format(null, priceQueryParamName));
  };

  // Toggle open state. When opening on mobile, announce so others unmount first.
  const toggleIsOpen = () => {
    setIsOpen(prev => {
      const next = !prev;

      return next;
    });
    onToggle?.();
  };

  // Click outside to close. Use 'click' so clicked element's handlers run first.
  useEffect(() => {
    const handleClickOutside = e => {
      if (isOpen && !dropdownRef.current?.contains(e.target)) {
        setIsOpen(false);
        onBlur?.();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, onBlur, isMobile, drawerKey]);

  const classes = classNames(rootClassName || css.root, { [css.rootOpen]: isOpen }, className);

  const priceQueryParam = getPriceQueryParamName(queryParamNames);
  const initialPrice = initialValues ? parse(initialValues[priceQueryParam]) : {};
  let { minPrice, maxPrice } = initialPrice || {};
  if (maxPrice) minPrice = 0;

  const hasValue = value => value != null;
  const hasInitialValues = initialValues && (hasValue(minPrice) || hasValue(maxPrice));

  const labelSelection = hasInitialValues
    ? intl.formatMessage(
        { id: 'PriceFilter.labelSelectedPlain' },
        {
          minPrice: minPrice && formatCurrencyMajorUnit(intl, marketplaceCurrency, minPrice),
          maxPrice: maxPrice && formatCurrencyMajorUnit(intl, marketplaceCurrency, maxPrice),
        }
      )
    : null;

  return (
    <div className={classes} ref={dropdownRef} data-drawer={drawerKey}>
      <div className={css.filterHeader}>
        <button
          type="button"
          className={classNames(css.labelButton, { [css.labelButtonOpened]: isOpen })}
          onClick={toggleIsOpen}
          aria-expanded={isOpen}
          aria-controls={`${drawerKey}-content`}
        >
          <span className={css.labelButtonContent}>
            {inputIcon && <span className={css.icon}>{inputIcon}</span>}
            <span className={css.labelWrapper}>
              <span className={css.label}>{`${label}${labelSelection ? ' (1)' : ''}`}</span>
            </span>
            <span className={classNames(css.openSign, { [css.openSignLeft]: isMobile && isOpen })}>
              <IconArrowHead
                direction={!isMobile ? (isOpen ? 'up' : 'down') : isOpen ? 'left' : 'right'}
              />
            </span>
          </span>
        </button>
      </div>

      <div className={css.divider} />

      <div
        id={`${drawerKey}-content`}
        className={classNames(css.formWrapper, { [css.formClosed]: !isOpen })}
      >
        <PriceFilterForm
          id={id}
          initialValues={hasInitialValues ? initialPrice : { minPrice: min }}
          onChange={handleChange}
          onCancel={() => {
            setIsOpen(false);
            if (isMobile) {
              window.dispatchEvent(
                new CustomEvent('myapp:drawer:closing', { detail: { id: drawerKey } })
              );
            }
          }}
          onSubmit={handleChange}
          onClear={handleClear}
          intl={intl}
          contentRef={node => {
            filterContent.current = node;
          }}
          min={min}
          step={step}
          liveEdit={true}
          isOpen={true}
          isInSideBar
          style={{ minWidth: '200px' }}
          currency={marketplaceCurrency}
        />
      </div>
    </div>
  );
};

const PriceFilterPlain = injectIntl(PriceFilterPlainComponent);

export default PriceFilterPlain;
